import React, { useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/ui-kit/button';
import { Input } from '@/ui-kit/input';
import { Label } from '@/ui-kit/label';
import { verifyMfaChallenge } from '@/services/authSecurity';

type MfaChallengeScreenProps = {
    onVerified: () => void;
};

export const MfaChallengeScreen: React.FC<MfaChallengeScreenProps> = ({ onVerified }) => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        setIsSubmitting(true);
        try {
            await verifyMfaChallenge(code.trim());
            onVerified();
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Verification failed. Please try again.';
            setError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-lg">
                <div className="mb-6 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="text-xl font-semibold text-foreground">
                            Two-factor verification
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Enter the code from your authenticator app.
                        </p>
                    </div>
                </div>

                <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="mfa-code">Authentication code</Label>
                        <Input
                            id="mfa-code"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="123456"
                            value={code}
                            onChange={(e) => setCode(e.target.value.trim())}
                            autoFocus
                            required
                        />
                    </div>
                    {error ? <p className="text-sm text-destructive">{error}</p> : null}
                    <Button type="submit" className="w-full" disabled={isSubmitting || code.length < 6}>
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Verifying...
                            </>
                        ) : (
                            'Verify and continue'
                        )}
                    </Button>
                </form>
            </div>
        </div>
    );
};
